/**
 * Water Report Popup
 * Professional modal with auto-show, form submission, and webhook integration
 */
(function() {
  'use strict';

  // Use global configuration from water-report-config.js
  // All configuration should be done in water-report-config.js
  const CONFIG = window.WaterReportConfig;

  // Exit if config is not loaded
  if (!CONFIG) {
    console.error('Water Report Popup: Configuration not found. Make sure water-report-config.js is loaded before this script.');
    return;
  }

  // Postcode validation state
  let isPostcodeValid = false;
  let validationTimeout = null;

  // Popup session ID for tracking user journey
  let popupSessionId = null;
  let impressionTracked = false;

  // [US/UK] These are set inside init() once DOM is ready
  let overlay, modal, closeBtn, form, submitBtn, zipcodeInput, market, marketStorageKey;

  /**
   * Validate postcode via postcodes.io API
   */
  async function validatePostcode(postcode) {
    const cleanPostcode = postcode.trim().replace(/\s+/g, '');

    if (cleanPostcode.length < 5) {
      return { valid: false, message: '' };
    }

    // [US/UK] US ZIP codes are validated locally (no external API needed)
    if (market === 'US') {
      const usZipPattern = /^[0-9]{5}(-[0-9]{4})?$/;
      const valid = usZipPattern.test(cleanPostcode);
      return { valid, message: valid ? '' : 'Invalid US ZIP code' };
    }

    try {
      const response = await fetch(
        `${CONFIG.postcodeApiUrl}/${encodeURIComponent(cleanPostcode)}/validate`
      );
      const data = await response.json();

      return {
        valid: data.result === true,
        message: data.result === true ? '' : 'Invalid UK postcode'
      };
    } catch (error) {
      console.error('Postcode validation error:', error);
      return { valid: false, message: 'Unable to validate postcode' };
    }
  }

  /**
   * Update postcode input UI based on validation state
   */
  function updatePostcodeUI(state) {
    if (!zipcodeInput) return;

    const formGroup = zipcodeInput.closest('.wr-form-group');
    let errorEl = formGroup?.querySelector('.wr-postcode-error');
    let successEl = formGroup?.querySelector('.wr-postcode-success');

    // Remove existing states
    zipcodeInput.classList.remove('is-valid', 'is-invalid', 'is-validating');

    if (state === 'validating') {
      zipcodeInput.classList.add('is-validating');
      if (errorEl) errorEl.textContent = '';
      if (successEl) successEl.style.display = 'none';
    } else if (state === 'valid') {
      zipcodeInput.classList.add('is-valid');
      if (errorEl) errorEl.textContent = '';
      if (successEl) successEl.style.display = 'block';
    } else if (state === 'invalid') {
      zipcodeInput.classList.add('is-invalid');
      if (successEl) successEl.style.display = 'none';
    } else {
      if (errorEl) errorEl.textContent = '';
      if (successEl) successEl.style.display = 'none';
    }

    updateSubmitButton();
  }

  /**
   * Show error message for postcode
   */
  function showPostcodeError(message) {
    if (!zipcodeInput) return;

    const formGroup = zipcodeInput.closest('.wr-form-group');
    let errorEl = formGroup?.querySelector('.wr-postcode-error');

    if (!errorEl && formGroup) {
      errorEl = document.createElement('span');
      errorEl.className = 'wr-postcode-error';
      formGroup.appendChild(errorEl);
    }

    if (errorEl) {
      errorEl.textContent = message;
    }
  }

  /**
   * Handle postcode input with debounce
   */
  function handlePostcodeInput() {
    isPostcodeValid = false;

    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    const value = zipcodeInput?.value.trim() || '';

    if (value.length < 5) {
      updatePostcodeUI('neutral');
      return;
    }

    updatePostcodeUI('validating');

    validationTimeout = setTimeout(async () => {
      const result = await validatePostcode(value);
      isPostcodeValid = result.valid;

      if (result.valid) {
        updatePostcodeUI('valid');
      } else {
        updatePostcodeUI('invalid');
        if (result.message) {
          showPostcodeError(result.message);
        }
      }
    }, CONFIG.debounceDelay);
  }

  /**
   * Update submit button state
   */
  function updateSubmitButton() {
    if (!submitBtn) return;

    const emailInput = form?.querySelector('#wr-email');
    const emailValid = emailInput?.validity.valid || false;
    const canSubmit = emailValid && isPostcodeValid;

    submitBtn.disabled = !canSubmit;
    submitBtn.classList.toggle('is-disabled', !canSubmit);
  }

  /**
   * Generate UUID v4 fallback
   */
  function generateUUIDv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Generate or retrieve popup session ID
   */
  function getPopupSessionId() {
    if (popupSessionId) {
      return popupSessionId;
    }

    try {
      // Check if ID already exists in localStorage
      const existingId = localStorage.getItem(CONFIG.sessionIdKey);
      if (existingId) {
        popupSessionId = existingId;
        return popupSessionId;
      }

      // Generate new UUID
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        popupSessionId = crypto.randomUUID();
      } else {
        popupSessionId = generateUUIDv4();
      }

      localStorage.setItem(CONFIG.sessionIdKey, popupSessionId);
      return popupSessionId;
    } catch (err) {
      // Fallback if localStorage is unavailable
      console.warn('Unable to store popup session ID:', err);
      popupSessionId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : generateUUIDv4();
      return popupSessionId;
    }
  }

  /**
   * Check if impression has already been tracked this session
   */
  function isImpressionTracked() {
    if (impressionTracked) {
      return true;
    }

    try {
      return sessionStorage.getItem(CONFIG.impressionTrackedKey) === 'true';
    } catch (err) {
      return false;
    }
  }

  /**
   * Mark impression as tracked
   */
  function markImpressionTracked() {
    impressionTracked = true;
    try {
      sessionStorage.setItem(CONFIG.impressionTrackedKey, 'true');
    } catch (err) {
      // Silent fail
    }
  }

  /**
   * Track popup impression to Supabase
   */
  async function trackPopupImpression() {
    if (isImpressionTracked()) {
      return;
    }

    const sessionId = getPopupSessionId();

    try {
      await fetch(CONFIG.supabaseImpressionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${CONFIG.supabaseAnonKey}`,
          'apikey': CONFIG.supabaseAnonKey
        },
        body: JSON.stringify({
          popup_session_id: sessionId
        })
      });

      markImpressionTracked();
    } catch (err) {
      console.error('Failed to track popup impression:', err);
    }
  }

  /**
   * Show the popup modal
   */
  function showPopup() {
    overlay.classList.add('is-visible');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Generate popup session ID on first show
    getPopupSessionId();

    // Track popup impression
    trackPopupImpression();

    // Reset form state
    isPostcodeValid = false;
    updateSubmitButton();

    // Focus first input for accessibility
    const firstInput = form.querySelector('input');
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 350);
    }

    // Trap focus inside modal
    document.addEventListener('keydown', handleKeydown);
  }

  /**
   * Hide the popup modal
   */
  function hidePopup() {
    overlay.classList.remove('is-visible');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    document.removeEventListener('keydown', handleKeydown);

    // Mark as dismissed for this session so it doesn't reappear on navigation
    try {
      sessionStorage.setItem(marketStorageKey, 'true');
    } catch (err) {}
  }

  /**
   * Handle keyboard events (Escape to close, Tab trap)
   */
  function handleKeydown(e) {
    if (e.key === 'Escape') {
      hidePopup();
      return;
    }

    // Tab trap
    if (e.key === 'Tab') {
      const focusableElements = modal.querySelectorAll(
        'button, input, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      } else if (!e.shiftKey && document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  /**
   * Handle overlay click (close if clicking outside modal)
   */
  function handleOverlayClick(e) {
    if (e.target === overlay) {
      hidePopup();
    }
  }

  /**
   * Track form submission to Supabase
   */
  async function trackFormSubmission(email, popupSessionId) {
    try {
      await fetch(CONFIG.supabaseFormSubmissionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${CONFIG.supabaseAnonKey}`,
          'apikey': CONFIG.supabaseAnonKey
        },
        body: JSON.stringify({
          popup_session_id: popupSessionId,
          email: email
        })
      });
    } catch (err) {
      console.error('Failed to track form submission:', err);
    }
  }

  /**
   * Handle form submission
   */
  async function handleSubmit(e) {
    e.preventDefault();

    const emailInput = form.querySelector('#wr-email');
    const phoneInput = form.querySelector('#wr-phone');

    if (!emailInput || !phoneInput || !zipcodeInput) return;

    // Double-check postcode validity before submit
    if (!isPostcodeValid) {
      showPostcodeError('Please enter a valid UK postcode');
      return;
    }

    const data = {
      email: emailInput.value.trim(),
      phone: phoneInput.value.trim(),
      zipcode: zipcodeInput.value.trim(),
      popup_session_id: getPopupSessionId(),
      source: 'popup',
      // [US/UK] Read market from form data-market attribute
      market: form.dataset.market || 'UK'
    };

    // Validate
    if (!data.email || !data.zipcode) {
      return;
    }

    // Show thank you message immediately
    const formContent = modal.querySelector('.wr-popup-content') || modal.querySelector('.wr-popup-body') || form.parentElement;
    if (formContent) {
      formContent.innerHTML = '<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:40px 24px;gap:12px;min-height:200px;"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#3863F6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></svg><h3 style="margin:0;font-size:22px;font-weight:600;color:#1a1a1a;">Your free water report has been claimed.</h3><p style="margin:0;font-size:15px;color:#666;line-height:1.5;">We\'re analysing your area now. Check your inbox in the next few minutes.</p></div>';
    }
    setTimeout(function() { hidePopup(); }, 2000);

    // Mark as submitted permanently (persists across browser sessions)
    try {
      localStorage.setItem(marketStorageKey + '_submitted', 'true');
      sessionStorage.setItem(marketStorageKey, 'true');
    } catch (err) {}

    // Fire-and-forget: send data to n8n and Supabase in background
    trackFormSubmission(data.email, data.popup_session_id);
    const webhookUrl = (market === 'US' && CONFIG.webhookUrlUS) ? CONFIG.webhookUrlUS : CONFIG.webhookUrl;
    fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).catch(function(err) { console.error('Water Report Popup: Webhook error', err); });
  }

  /**
   * Check if popup should be shown
   */
  function shouldShowPopup() {
    try {
      // Don't show if already submitted (permanent, survives browser close)
      if (localStorage.getItem(marketStorageKey + '_submitted')) return false;
      // Don't show if dismissed or shown this session
      if (sessionStorage.getItem(marketStorageKey)) return false;
      return true;
    } catch (err) {
      return true;
    }
  }

  /**
   * Initialize popup
   */
  function init() {
    console.log('[WR Popup] init() called');

    // DOM Elements — read here to guarantee DOM is ready
    overlay = document.getElementById('water-report-overlay');
    modal = overlay?.querySelector('.wr-popup-modal');
    closeBtn = overlay?.querySelector('.wr-popup-close');
    form = document.getElementById('wr-popup-form');
    submitBtn = form?.querySelector('.wr-form-submit');
    zipcodeInput = form?.querySelector('#wr-zipcode');

    console.log('[WR Popup] overlay:', overlay, '| form:', form);

    if (!overlay || !modal || !form) {
      console.warn('[WR Popup] Required elements not found — aborting');
      return;
    }

    // [US/UK] Detect market from Shopify.country, set data-market on form
    const shopifyCountry = (typeof Shopify !== 'undefined' && Shopify.country) ? Shopify.country : null;
    console.log('[WR Popup] Shopify.country:', shopifyCountry);

    const ALLOWED_COUNTRIES = ['GB', 'US'];
    if (!shopifyCountry || !ALLOWED_COUNTRIES.includes(shopifyCountry)) {
      console.warn('[WR Popup] Country not allowed:', shopifyCountry, '— aborting');
      return;
    }
    market = shopifyCountry === 'GB' ? 'UK' : 'US';
    form.dataset.market = market;
    marketStorageKey = CONFIG.storageKey + '_' + market.toLowerCase();
    console.log('[WR Popup] market:', market, '| marketStorageKey:', marketStorageKey);

    // Adapt form labels and placeholders for US market
    if (market === 'US') {
      const phoneInput = form.querySelector('#wr-phone');
      const zipcodeLabel = form.querySelector('label[for="wr-zipcode"]');
      if (phoneInput) phoneInput.placeholder = '+1 555 000 0000';
      if (zipcodeInput) {
        zipcodeInput.placeholder = '10001';
        zipcodeInput.pattern = '[0-9]{5}(-[0-9]{4})?';
        zipcodeInput.maxLength = 10;
      }
      if (zipcodeLabel) zipcodeLabel.textContent = 'ZIP code';
    }

    // Event listeners
    closeBtn?.addEventListener('click', hidePopup);
    overlay.addEventListener('click', handleOverlayClick);
    form.addEventListener('submit', handleSubmit);

    // Postcode validation on input
    zipcodeInput?.addEventListener('input', handlePostcodeInput);

    // Email input triggers submit button update
    const emailInput = form?.querySelector('#wr-email');
    emailInput?.addEventListener('input', updateSubmitButton);

    // Initially disable submit button
    updateSubmitButton();

    // Auto-show after delay (only if not already shown this session)
    console.log('[WR Popup] shouldShowPopup:', shouldShowPopup(), '| sessionStorage key:', marketStorageKey, '=', sessionStorage.getItem(marketStorageKey));
    if (shouldShowPopup()) {
      // Check if market is enabled via app toggle
      checkMarketEnabled(market).then(function(enabled) {
        if (!enabled) {
          console.warn('[WR Popup] Market', market, 'is disabled via admin toggle — skipping');
          return;
        }
        console.log('[WR Popup] Market', market, 'is enabled — scheduling showPopup in', CONFIG.showDelay, 'ms');
        setTimeout(showPopup, CONFIG.showDelay);
      });
    } else {
      console.warn('[WR Popup] Popup already shown this session — skipping');
    }
  }

  /**
   * Check if market popup is enabled via the admin dashboard toggle.
   * Calls /apps/water-report?action=market-status&market=XX
   * Returns true by default if the check fails (fail-open).
   */
  async function checkMarketEnabled(mkt) {
    try {
      const resp = await fetch('/apps/water-report?action=market-status&market=' + encodeURIComponent(mkt));
      if (!resp.ok) return true;
      const data = await resp.json();
      return data.enabled !== false;
    } catch (err) {
      console.warn('[WR Popup] Market status check failed, defaulting to enabled:', err);
      return true;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
